import { produce } from 'immer';

import {
  IOrientationInfo,
  IPersistedState,
  IReportInfo,
  IState,
  ITabProperties,
  appInitialState,
} from 'appRedux/createStore';
import moduleReducerRegistry from './moduleReducerRegistry';
import { actionTypes } from 'appRedux/actions/commandCreator';
import { IPlayerCardInfo, IUserInfo } from 'services/auth';
import { AUTH_STATE, REPORT_TYPE_ENUM, AuthStateType } from 'appRedux/types/reduxTypes';
import { floormapInitialState } from './floormapReducer';
import { slotInitialState } from './slotReducer';
import {
  IFavoritesState,
  InsightDisplayType,
  InsightFilterEnum,
  TimeframeIdFilterEnum,
} from 'appRedux/models/favoritesModels';
import { IPlaylistState } from 'appRedux/models/playlistModels';
import { InsightValue, TimeFrameValue } from 'common/types';
import { config } from 'config/config';
import { ICasinoSchemaWithAccess } from 'appRedux/models/casinoModels';
import { slotCompareInitialState } from './slotCompareReducer';
import { SupportedPhoneCountryConfig } from 'common/phoneCountries';
import { clearApplicationConfigurationsAll } from 'lib/applicationConfig';

export interface IAction {
  type: string;
  state?: any;
}

const historyResetInitialState = {
  activeSlotNumber: 0,
  slotNumbers: [],
};

const defaultFilters: ITabProperties = {
  timeframe: 'months1' as TimeFrameValue,
  insight: 'return' as InsightValue, // Changed from 'spins' — free subscriptions now start with RTP
  denominations: config.denom,
  advancedViewInsights: [
    'pop',
    'return',
    'spins',
    'jackpot_value',
    'money_played',
  ] as InsightValue[],
};

const resetInitailState = {
  leaderboard: { ...defaultFilters },
  jackpot: { ...defaultFilters, insight: 'jackpot_value' as InsightValue },
  myplay: { ...defaultFilters },
  graphfilter: { ...defaultFilters },
  mapfilter: { ...defaultFilters },
  snapfilter: { ...defaultFilters },
  main: { ...defaultFilters },
  hottestSlots: { ...defaultFilters },
};

const favoritesResetInitialState: IFavoritesState = {
  favoritesList: [],
  insightFilter: InsightFilterEnum.Spins,
  displayType: InsightDisplayType.Number,
  timeframeFilter: TimeframeIdFilterEnum.ThirtyDays,
};

const playlistResetInitialState: IPlaylistState = {
  playlist: [],
};

const resetInitialState: IPersistedState = {
  userInfo: {
    lastUpdateDateCollapse: false,
    hasCompletedOnboarding: false,
  },
  authState: AUTH_STATE.SIGN_IN,
  emailAlertState: false,
  reportType: {
    id: REPORT_TYPE_ENUM.MAPS,
  },
  hasUserConfirm: true,
  historyState: historyResetInitialState,
  isSlotOrderAscending: true,
  linkPlayerState: {
    casinoLinkedAccounts: [],
    isPlayerLinked: false,
    linkedCasinoId: null,
  },
  favorites: favoritesResetInitialState,
  playlist: playlistResetInitialState,
  filterState: resetInitailState,
  floormapState: floormapInitialState,
  slotState: slotInitialState,
  compareSlotsState: slotCompareInitialState,
};

const resetClearInitialState = {
  reportType: {
    id: REPORT_TYPE_ENUM.MAPS,
  },
  hasUserConfirm: true,
  historyState: historyResetInitialState,
  isSlotOrderAscending: true,
  linkPlayerState: {
    casinoLinkedAccounts: [],
    isPlayerLinked: false,
    linkedCasinoId: null,
  },
  favorites: favoritesResetInitialState,
  playlist: playlistResetInitialState,
  filterState: resetInitailState,
  floormapState: floormapInitialState,
  slotState: slotInitialState,
  compareSlotsState: slotCompareInitialState,
};

const app = produce((draft: IState, action: IAction) => {
  if (!draft) return appInitialState;

  const { type, state } = action;

  switch (type) {
    case actionTypes.SET_AUTH_STATE:
      draft.app.persistedState.authState = state as AuthStateType;
      break;
    case actionTypes.SET_SUPPORTED_PHONE_COUNTRIES_STATE:
      draft.app.persistedState.supportedPhoneCountries = state as SupportedPhoneCountryConfig[];
      break;
    case actionTypes.SET_TOUR_STATE:
      if (draft.app.persistedState.userInfo) {
        draft.app.persistedState.userInfo.hasCompletedOnboarding = state as boolean;
      }
      break;
    case actionTypes.SET_USER_PRIVACY:
      if (draft.app.persistedState.userInfo) {
        draft.app.persistedState.userInfo.isPublicVisible = state as boolean;
      }
      break;
    case actionTypes.SET_USERINFO_STATE:
      draft.app.persistedState.userInfo = {
        ...draft.app.persistedState.userInfo,
        ...(state as IUserInfo),
      };
      break;
    case actionTypes.INIT_USERINFO_STATE:
      draft.app.persistedState.userInfo = {
        ...(state as IUserInfo),
      };
      break;
    case actionTypes.SET_USER_ALIAS_STATE: {
      const { aliasInitial, aliasInitial2, aliasName } = state as IUserInfo;
      if (draft.app.persistedState.userInfo) {
        draft.app.persistedState.userInfo.aliasInitial = aliasInitial;
        draft.app.persistedState.userInfo.aliasInitial2 = aliasInitial2;
        draft.app.persistedState.userInfo.aliasName = aliasName;
      }
      break;
    }
    case actionTypes.SET_USER_INFORMATION_STATE: {
      const { firstName, lastName, emailAddress, isCasinoAccount } = state as IUserInfo;
      if (draft.app.persistedState.userInfo) {
        draft.app.persistedState.userInfo.firstName = firstName ?? '';
        draft.app.persistedState.userInfo.lastName = lastName ?? '';
        draft.app.persistedState.userInfo.emailAddress = emailAddress ?? '';

        if (isCasinoAccount) {
          draft.app.persistedState.userInfo.isCasinoAccount = isCasinoAccount;
        }
        // Opt-in reels (users.showLoadingReels): only Login / GetMe send the key, so it
        // is replaced (or cleared) there; other callers (Edit Profile, ...) leave it.
        if (state && typeof state === 'object' && 'showLoadingReels' in state) {
          draft.app.persistedState.userInfo.showLoadingReels =
            (state as IUserInfo).showLoadingReels ?? null;
        }
      }
      break;
    }
    case actionTypes.SET_USERINFO_LEVEL_STATE:
      draft.app.persistedState.userInfo = {
        ...draft.app.persistedState.userInfo,
        ...(state as IUserInfo),
      };
      break;
    case actionTypes.SET_USER_AVATAR_STATE: {
      const { avatarId, avatarBgId } = state as IUserInfo;
      if (draft.app.persistedState.userInfo) {
        draft.app.persistedState.userInfo.avatarId = avatarId;
        draft.app.persistedState.userInfo.avatarBgId = avatarBgId;
      }
      break;
    }
    case actionTypes.SET_PLAYER_CARD_INFO:
      draft.app.persistedState.playerCardInfo = state as IPlayerCardInfo | null;
      break;
    case actionTypes.SET_AWS_JWK_STATE:
      draft.app.authJWK = state as string;
      break;
    case actionTypes.CLEAR_STORE:
      {
        // Preserve last-viewed casino (casinoSchema) across logout so login can
        // resume into it. supportedPhoneCountries kept as before.
        const supportedPhoneCountries = draft.app.persistedState.supportedPhoneCountries;
        const casinoSchema = draft.app.persistedState.casinoSchema;
      draft.app.authJWK = '';
      draft.app.InactivityAlertState = false;
      draft.app.applicationConfigurations = [];
      clearApplicationConfigurationsAll();
      draft.app.persistedState = {
        ...resetInitialState,
        supportedPhoneCountries,
        ...(casinoSchema ? { casinoSchema } : {}),
      };
      break;
      }
    case actionTypes.CLEAR_RESET_INITIALSTATE:
      draft.app.persistedState = {
        ...resetClearInitialState,
        supportedPhoneCountries: draft.app.persistedState.supportedPhoneCountries,
      };
      break;
    case actionTypes.SET_CLIENT_SECRET:
      draft.app.clientSecret = state as string;
      break;
    case actionTypes.SET_EMAIL_ALERT_STATE:
      draft.app.persistedState.emailAlertState = state as boolean;
      break;
    case actionTypes.SET_REPORT_TYPE_STATE:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      draft.app.persistedState.reportType = {
        ...draft.app.persistedState.reportType,
        ...(state as object),
      } as IReportInfo;
      break;
    case actionTypes.SET_ORIENTATION:
      draft.app.persistedState.orientationState = state as IOrientationInfo;
      break;
    case actionTypes.SET_SLOT_ORDER:
      draft.app.persistedState.isSlotOrderAscending = state as boolean;
      break;
    case actionTypes.SET_USER_CONFIRM:
      draft.app.persistedState.hasUserConfirm = state as boolean;
      break;
    case actionTypes.SET_USER_PRIMARY_CASINO:
      draft.app.persistedState.userInfo = {
        ...draft.app.persistedState.userInfo,
        primaryCasino: state as ICasinoSchemaWithAccess[],
      };
      break;
    case actionTypes.SET_PLAYER_CARD_INFO:
      draft.app.persistedState.playerCardInfo = state as IPlayerCardInfo | null;
      break;
    default:
      // Module reducers
      moduleReducerRegistry.forEach((reducer) => {
        reducer(draft, action);
      });
  }

  return draft;
});

export default app;
